import ReactDOM from "react-dom/client";
import "bootstrap/dist/css/bootstrap.min.css";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { createBrowserRouter, RouterProvider } from "react-router";
import "moment/locale/fr";
import { Provider } from "react-redux";
import { store } from "./store";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import SiteDetails from "./screens/sites/SiteDetails";
import Sites from "./screens/sites/Sites";
import Contacts from "./screens/contacts/Contacts";
import Planning from "./screens/planning/Planning";
import {
  apiGetSiteContacts,
  apiGetSiteDetails,
  apiGetSiteEvents,
  apiGetSiteFiles,
  apiGetSitePrestations, apiGetSiteProgressReports,
  apiGetSites
} from "./api/ApiSites";
import { apiGetContext } from "./api/ApiApp";
import { apiGetContacts } from "./api/ApiContacts";
import Memos from "./screens/memos/Memos";
import Vehicules from "./screens/vehicules/Vehicules";
import Purchases from "./screens/purchases/Purchases";
import { apiGetMemos } from "./api/ApiMemos";
import { apiGetVehicules } from "./api/ApiVehicules";
import { apiGetFolders } from "./api/ApiFolders";
import { apiGetPlanningEvents, apiGetPlanningPrestations, apiGetPlanningSites } from "./api/ApiPlanning";
import Signin from "./screens/authentication/signin/Signin";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import moment from "moment/moment";
import Parameters from "./screens/parameters/Parameters";
import PlanningWeek from "./screens/planning/PlanningWeek";
import { monthRange, MONTH_FORMAT } from "./screens/planning/planningData";
import { currentPlanningWeekMode, DAY_MODE } from "./screens/planning/planningWeekMode";
import { apiGetPurchases } from "./api/ApiPurchases";

const root = ReactDOM.createRoot(document.getElementById("root"));

const queryClient = new QueryClient();

if (process.env.NODE_ENV === 'development') {
  window.__TANSTACK_QUERY_CLIENT__ = queryClient;
}

const auth = getAuth();

onAuthStateChanged(auth, (user) => {
  let router;

  if (user) {
    router = createBrowserRouter([
      {
        path: "/",
        element: <App/>,
        loader: () => {
          return queryClient.query(apiGetContext());
        },
        children: [
          {
            path: "/",
            element: <PlanningWeek/>,
            loader: () => {
              // The months around today, loaded month by month like the planning does when scrolling
              const months = [-1, 0, 1].map(offset => moment().add(offset, "month").format(MONTH_FORMAT));

              return Promise.all([
                queryClient.query(apiGetPlanningSites()),
                ...months.map(month => queryClient.query(apiGetPlanningEvents(monthRange(month)))),
                ...months.map(month => queryClient.query(apiGetPlanningPrestations(monthRange(month))))
              ]);
            }
          },
          {
            path: "/planning/:year/:month/:day",
            element: <PlanningWeek/>,
            // Runs on every day change (the day is in the URL): the page shows the new day once its data is there
            loader: ({params}) => {
              const {day, month, year} = params;
              const dayKey = moment(year + "-" + month + "-" + day, "YYYY-MM-DD").format("YYYY-MM-DD");

              const loads = [queryClient.query(apiGetPlanningSites())];

              if (currentPlanningWeekMode() === DAY_MODE) {
                // "Jour": the day's data, loaded again on every visit
                const range = {from: dayKey, to: dayKey};

                loads.push(
                  queryClient.query(apiGetPlanningEvents(range)),
                  queryClient.query(apiGetPlanningPrestations(range))
                );
              } else {
                // Several days: the months around the day, unless they are already loaded
                const months = [-1, 0, 1].map(offset => moment(dayKey).add(offset, "month").format(MONTH_FORMAT));

                months.forEach(month => loads.push(
                  queryClient.query({...apiGetPlanningEvents(monthRange(month)), staleTime: "static"}),
                  queryClient.query({...apiGetPlanningPrestations(monthRange(month)), staleTime: "static"})
                ));
              }

              return Promise.all(loads);
            }
          },
          {
            path: "/sites",
            element: <Sites/>,
            loader: ({request}) => {
              const url = new URL(request.url);

              const searchParams = url.searchParams;

              const queryParams = {size: 50, page: +searchParams.get("page") || 0};

              if (searchParams.get("filter")) {
                queryParams["filter"] = searchParams.get("filter");
              }

              if (searchParams.get("active")) {
                queryParams["active"] = searchParams.get("active") === "1";
              }

              return queryClient.query(apiGetSites(queryParams));
            }
          },
          {
            path: "/sites/:id",
            element: <SiteDetails/>,
            loader: ({params}) => {
              return Promise.all([
                queryClient.query(apiGetSiteDetails(params.id)),
                queryClient.query(apiGetSitePrestations(params.id)),
                queryClient.query(apiGetSiteContacts(params.id)),
                queryClient.query(apiGetSiteEvents(params.id)),
                queryClient.query(apiGetSiteFiles(params.id)),
                queryClient.query(apiGetSiteProgressReports(params.id)),
              ]);
            }
          },
          {
            path: "/contacts",
            element: <Contacts/>,
            loader: ({request}) => {
              const url = new URL(request.url);

              const searchParams = url.searchParams;

              const queryParams = {size: 50, page: +searchParams.get("page") || 0};

              if (searchParams.get("filter")) {
                queryParams["filter"] = searchParams.get("filter");
              }

              return queryClient.query(apiGetContacts(queryParams));
            }
          },
          {
            path: "/purchases",
            element: <Purchases/>,
            loader: ({request}) => {
              const url = new URL(request.url);

              const searchParams = url.searchParams;

              const queryParams = {size: 50, page: +searchParams.get("page") || 0};

              if (searchParams.get("filter")) {
                queryParams["filter"] = searchParams.get("filter");
              }

              return queryClient.query(apiGetPurchases(queryParams));
            }
          },
          {
            path: "/memos",
            element: <Memos/>,
            loader: ({request}) => {
              const url = new URL(request.url);

              const searchParams = url.searchParams;

              const queryParams = {size: 50, page: +searchParams.get("page") || 0};

              if (searchParams.get("filter")) {
                queryParams["filter"] = searchParams.get("filter");
              }

              return Promise.all([
                queryClient.query(apiGetMemos(queryParams)),
                queryClient.query(apiGetFolders()),
              ]);
            }
          },
          {
            path: "/memos/f/:folderId",
            element: <Memos/>,
            loader: ({params, request}) => {
              const url = new URL(request.url);

              const searchParams = url.searchParams;

              const queryParams = {size: 50, page: +searchParams.get("page") || 0, folderId: params.folderId};

              if (searchParams.get("filter")) {
                queryParams.filter = searchParams.get("filter");
              }

              return Promise.all([
                queryClient.query(apiGetMemos(queryParams)),
                queryClient.query(apiGetFolders()),
              ]);
            }
          },
          {
            path: "/vehicules",
            element: <Vehicules/>,
            loader: ({request}) => {
              const url = new URL(request.url);

              const searchParams = url.searchParams;

              const queryParams = {size: 50, page: +searchParams.get("page") || 0};

              if (searchParams.get("filter")) {
                queryParams["filter"] = searchParams.get("filter");
              }

              return queryClient.query(apiGetVehicules(queryParams));
            }
          },
          {
            path: "/parameters",
            element: <Parameters/>,
          }
        ]
      }
    ]);
  } else {
    router = createBrowserRouter([
      {path: "", element: <Signin/>}
    ]);
  }

  root.render(
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router}/>
      </QueryClientProvider>
    </Provider>
  );
});


// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
